import React, { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'

import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import FullWidthCarousel from 'LuxKit/Carousel/FullWidthCarousel'
import useTourFacetLocationCategory from 'hooks/TourV2/useTourFacetLocationCategory'
import { TOURS_GROUP_TYPE, TOURS_GROUP_TYPES_LIST } from 'constants/tours'
import config from 'constants/config'
import TourPageTailorMadeTourCategoryTile from 'components/Tours/TourPageTailorMadeTourCategoryTile'
import TourPageSearchTourCategoryTile from 'components/Tours/TourPageSearchTourCategoryTile'
import { LUXURY_ESCAPES } from 'constants/brands'

function getCategories(intl: IntlShape): Array<App.Tours.CategoryData> {
  return [
    {
      id: 'signature_series',
      name: intl.formatMessage(
        {
          id: 'DZjCb5CJ2f',
          defaultMessage: 'Signature Series',
          description: 'Tour category name for the Signature Series collection',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'KNJIFH0Jgr',
          defaultMessage: 'An experience like no other',
          description: 'Subtext for the Signature Series tour category',
        },
      ),
      imageId: 'tour-category-tile-signature-serie',
      show: config.BRAND === LUXURY_ESCAPES,
      parameterName: 'link',
      parameterValue: '/campaign/signature-series',
    },
    {
      id: 'tailor_made_tours',
      name: intl.formatMessage(
        {
          id: 'RLmkmQOZvU',
          defaultMessage: 'Tailor Made Tours',
          description: 'Tour category name for custom tailor-made tours',
        },
      ),
      subText: '',
      imageId: 'tour-category-tile-tailor-made-tour',
      show: config.TAILOR_MADE_TOURS_ENABLED,
    },
    {
      id: 'deluxe_tours',
      name: intl.formatMessage(
        {
          id: 'lC/yeKlMqY',
          defaultMessage: 'Deluxe Tours',
          description: 'Tour category name for deluxe-tier tours',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'L4miCi+QWT',
          defaultMessage: 'Discover the highlights, effortlessly',
          description: 'Subtext for the Deluxe Tours category',
        },
      ),
      imageId: 'tour-category-tile-deluxe',
      show: true,
      parameterName: 'tourStyles',
      parameterValue: ['Deluxe'],
    },
    {
      id: 'premium_tours',
      name: intl.formatMessage(
        {
          id: 'Y+T1yiRMvG',
          defaultMessage: 'Premium Tours',
          description: 'Tour category name for premium-tier tours',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'sKZGmQlhVU',
          defaultMessage: 'Elevated experiences, elevated accommodation',
          description: 'Subtext for the Premium Tours category',
        },
      ),
      imageId: 'tour-category-tile-premium-v2',
      show: true,
      parameterName: 'tourStyles',
      parameterValue: ['Premium'],
    },
    {
      id: 'ultra_lux_tours',
      name: intl.formatMessage(
        {
          id: 'xYL5jH5B5d',
          defaultMessage: 'Ultra Lux Tours',
          description: 'Tour category name for ultra luxury tours',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'mHH0xm8I66',
          defaultMessage: 'Only the best, from start to finish',
          description: 'Subtext for the Ultra Lux Tours category',
        },
      ),
      imageId: 'tour-category-tile-ultra-lux-v2',
      show: true,
      parameterName: 'tourStyles',
      parameterValue: ['Ultralux'],
    },
    {
      id: 'private_journeys',
      name: intl.formatMessage(
        {
          id: 'CRnwGKlCY9',
          defaultMessage: 'Private Journeys',
          description: 'Tour category name for private journey tours',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'j9s1knKMSe',
          defaultMessage: 'An intimate journey with just you and your partner',
          description: 'Subtext for the Private Journeys category',
        },
      ),
      imageId: 'tour-category-tile-private-journey',
      show: true,
      parameterName: TOURS_GROUP_TYPE.isPrivateRequest,
      parameterValue: true,
    },
    {
      id: 'food_and_culinary',
      name: intl.formatMessage(
        {
          id: 'v5bJVRy7oT',
          defaultMessage: 'Food & Culinary',
          description: 'Tour category name for food and culinary tours',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'ElL1HI3ZTT',
          defaultMessage: 'Taste your way around the world',
          description: 'Subtext for the Food & Culinary category',
        },
      ),
      imageId: 'tour-category-tile-food-alt',
      show: false,
      parameterName: 'categories',
      parameterValue: ['foodandculinarytours'],
    },
    {
      id: 'rail_journeys',
      name: intl.formatMessage(
        {
          id: 'sZ8tgEBUIl',
          defaultMessage: 'Rail Journeys',
          description: 'Tour category name for rail journey tours',
        },
      ),
      subText: intl.formatMessage(
        {
          id: '9bCmw33MfB',
          defaultMessage: 'All aboard to see the sights',
          description: 'Subtext for the Rail Journeys category',
        },
      ),
      imageId: 'tour-category-tile-rail-journeys',
      show: false,
      parameterName: 'categories',
      parameterValue: ['railjourneys'],
    },
    {
      id: 'cultural',
      name: intl.formatMessage(
        {
          id: 'pqeWl5W8Kc',
          defaultMessage: 'Cultural',
          description: 'Tour category name for cultural tours',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'vjKsKbKBwi',
          defaultMessage: 'See the sights & dive into culture',
          description: 'Subtext for the Cultural tours category',
        },
      ),
      imageId: 'tour-category-tile-cultural',
      show: false,
      parameterName: 'categories',
      parameterValue: ['culturaltours'],
    },
    {
      id: 'widlife_safari',
      name: intl.formatMessage(
        {
          id: 'C+sf1HMNxz',
          defaultMessage: 'Wildlife Safari',
          description: 'Tour category name for wildlife safari tours',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'Cs8DIKvWha',
          defaultMessage: 'Roam amongst diverse landscapes & wildlife',
          description: 'Subtext for the Wildlife Safari category',
        },
      ),
      imageId: 'tour-category-tile-wildlife',
      show: false,
      parameterName: 'categories',
      parameterValue: ['wildlifesafaris'],
    },
    {
      id: 'lux_exclusive',
      name: intl.formatMessage(
        {
          id: 'TxlMMKgor5',
          defaultMessage: 'Lux Exclusive',
          description: 'Tour category name for Luxury Escapes exclusive tours',
        },
      ),
      subText: intl.formatMessage(
        {
          id: '/eHPOwIVxF',
          defaultMessage: 'Exclusive to Luxury Escapes',
          description: 'Subtext for the Lux Exclusive category, indicating tours only available through Luxury Escapes',
        },
      ),
      imageId: 'tour-category-tile-lux-exclusive',
      show: false,
      parameterName: 'categories',
      parameterValue: ['luxexclusive'],
    },
    {
      id: 'family_friendly',
      name: intl.formatMessage(
        {
          id: 'xuBEYH/P1J',
          defaultMessage: 'Family Friendly',
          description: 'Tour category name for family-friendly tours',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'WgNFVd15ex',
          defaultMessage: 'Adventures for the whole family',
          description: 'Subtext for the Family Friendly category',
        },
      ),
      imageId: 'tour-category-tile-family',
      show: false,
      parameterName: TOURS_GROUP_TYPE.isFamilyFriendly,
      parameterValue: true,
    },
    {
      id: 'hiking_cycling_adventure',
      name: intl.formatMessage(
        {
          id: 'dQZeL2v8/G',
          defaultMessage: 'Hiking, Cycling & Adventure',
          description: 'Tour category name for hiking, cycling and adventure tours',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'YO4G0jPjBF',
          defaultMessage: 'Explore the great outdoors',
          description: 'Subtext for the Hiking, Cycling & Adventure category',
        },
      ),
      imageId: 'tour-category-tile-adventure',
      show: false,
      parameterName: 'categories',
      parameterValue: ['hikingcyclingandadventure'],
    },
    {
      id: 'festivals_and_events',
      name: intl.formatMessage(
        {
          id: 'XBNqcQd8iw',
          defaultMessage: 'Festivals & Events',
          description: 'Tour category name for festivals and events tours',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'ZomUSgC0tA',
          defaultMessage: 'Don’t miss out on life’s best moments',
          description: 'Subtext for the Festivals & Events category',
        },
      ),
      imageId: 'tour-category-tile-festival',
      show: false,
      parameterName: 'categories',
      parameterValue: ['festivalsandevents'],
    },
    {
      id: 'sailing',
      name: intl.formatMessage(
        {
          id: 'LuoxMfbvWl',
          defaultMessage: 'Sailing',
          description: 'Tour category name for sailing tours',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'fZ2W2ZbntC',
          defaultMessage: 'Hop on board to explore the seas',
          description: 'Subtext for the Sailing category',
        },
      ),
      imageId: 'tour-category-tile-sailing',
      show: false,
      parameterName: 'categories',
      parameterValue: ['sailing'],
    },
    {
      id: 'health_and_wellness',
      name: intl.formatMessage(
        {
          id: 'oXRdmwrzgc',
          defaultMessage: 'Health & Wellness',
          description: 'Tour category name for health and wellness tours',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'Ex2DkLWXcU',
          defaultMessage: 'Relax & rejuvenate',
          description: 'Subtext for the Health & Wellness category',
        },
      ),
      imageId: 'tour-category-tile-health',
      show: false,
      parameterName: 'categories',
      parameterValue: ['healthandwellness'],
    },
  ]
}

interface Props {
  title?: React.ReactNode
}

function TourPageCategoriesCarousel(props: Props) {
  const { title } = props
  const intl = useIntl()

  const tourFacets = useTourFacetLocationCategory({})

  const allCategories = useMemo(() => getCategories(intl), [intl])

  const categories = useMemo(() => {
    return allCategories.filter(category => {
      if (category.show) {
        if (category.parameterName === 'link') {
          return !!category.parameterValue
        } else if (category.parameterName === 'categories') {
          return tourFacets.categories.has(category.parameterValue[0])
        } else if (category.parameterName === 'productTypes') {
          return tourFacets.productTypes.has(category.parameterValue)
        } else if (category.parameterName === 'tourStyles') {
          return tourFacets.tourStyles.has(category.parameterValue[0])
        } else if (TOURS_GROUP_TYPES_LIST.includes(category.parameterName as TOURS_GROUP_TYPE)) {
          return tourFacets.groupTypes.has(category.parameterName ?? '')
        }
        return true
      }
      return false
    })
  }, [tourFacets, allCategories])

  return <VerticalSpacer gap={20}>
    <LayoutContainer>
      <Heading variant="heading2">
        {title ?? (
          <FormattedMessage
            id="dDcT1LGpw9"
            defaultMessage="Explore <i>your</i> way"
            description="Default heading for the tour categories carousel, with 'your' in italics"
            values={{ i: (chunks) => <i>{chunks}</i> }}
          />
        )}
      </Heading>
    </LayoutContainer>
    <FullWidthCarousel snap="start" itemsPerArrow={6}>
      {categories.map(category => {
        if (category.id === 'tailor_made_tours') {
          return <TourPageTailorMadeTourCategoryTile key={category.id} category={category} />
        } else {
          return <TourPageSearchTourCategoryTile key={category.id} category={category} />
        }
      })}
    </FullWidthCarousel>
  </VerticalSpacer>
}

export default React.memo(TourPageCategoriesCarousel)
